import { useLocale, useT } from "../../i18n";
import { formatPriceShort, fmt } from "../../lib/format";

const PRICE_STEPS = [1, 2, 3, 5, 7.5, 10, 15, 20, 30, 50].map((m) => m * 1_000_000);
const MILEAGE_STEPS = [10000, 30000, 50000, 80000, 100000, 150000];
const CC_STEPS = [660, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000, 6000];

export const FieldLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="micro-label mb-2 block opacity-55">{children}</label>
);

export const NativeSelect = ({ id, value, onChange, children, disabled, testId, className = "" }) => (
  <div className={`relative ${className}`}>
    <select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-11 w-full appearance-none rounded-none border border-ink/20 bg-white pl-3.5 pr-9 text-[15px] text-ink transition-colors duration-300 hover:border-ink/50 focus:border-ink focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 lg:text-sm"
    >
      {children}
    </select>
    <span className="pointer-events-none absolute right-3.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-b border-r border-ink/60" aria-hidden />
  </div>
);

export const MakeModelFields = ({ value, onChange, facets, idPrefix }) => {
  const t = useT();
  const make = facets?.makes.find((m) => m.value === value.make);
  return (
    <div className="grid gap-4">
      <div>
        <FieldLabel htmlFor={`${idPrefix}-make`}>{t("filters.make")}</FieldLabel>
        <NativeSelect id={`${idPrefix}-make`} testId={`${idPrefix}-make-select`} value={value.make} onChange={(v) => onChange({ make: v, model: "" })}>
          <option value="">{t("filters.allMakes")}</option>
          {facets?.makes.map((m) => <option key={m.value} value={m.value}>{`${m.label || m.value} (${m.count})`}</option>)}
        </NativeSelect>
      </div>
      <div>
        <FieldLabel htmlFor={`${idPrefix}-model`}>{t("filters.model")}</FieldLabel>
        <NativeSelect id={`${idPrefix}-model`} testId={`${idPrefix}-model-select`} value={value.model} disabled={!make} onChange={(v) => onChange({ model: v })}>
          <option value="">{make ? t("filters.allModels") : t("filters.selectMakeFirst")}</option>
          {make?.models.map((m) => <option key={m.value} value={m.value}>{`${m.label || m.value} (${m.count})`}</option>)}
        </NativeSelect>
      </div>
    </div>
  );
};

const RangePair = ({ idPrefix, name, fromKey, toKey, value, onChange, options, render }) => {
  const t = useT();
  return (
    <div className="grid grid-cols-2 gap-3">
      {[[fromKey, "filters.from"], [toKey, "filters.to"]].map(([k, label]) => (
        <div key={k}>
          <FieldLabel htmlFor={`${idPrefix}-${k}`}>{t(label)}</FieldLabel>
          <NativeSelect id={`${idPrefix}-${k}`} testId={`${idPrefix}-${name}-${k}`} value={value[k]} onChange={(v) => onChange({ [k]: v })}>
            <option value="">{t("filters.any")}</option>
            {options.map((o) => <option key={o} value={o}>{render(o)}</option>)}
          </NativeSelect>
        </div>
      ))}
    </div>
  );
};

export const YearFields = ({ value, onChange, facets, idPrefix }) => {
  const { min, max } = facets?.year || {};
  const years = min && max ? Array.from({ length: max - min + 1 }, (_, i) => max - i) : [];
  return <RangePair idPrefix={idPrefix} name="year" fromKey="year_from" toKey="year_to" value={value} onChange={onChange} options={years} render={(y) => y} />;
};

export const DisplacementFields = ({ value, onChange, idPrefix }) => (
  <RangePair idPrefix={idPrefix} name="cc" fromKey="displacement_min" toKey="displacement_max" value={value} onChange={onChange} options={CC_STEPS} render={(c) => `${c.toLocaleString("en-US")} cc`} />
);

export const PriceFields = ({ value, onChange, idPrefix }) => {
  const lang = useLocale();
  return <RangePair idPrefix={idPrefix} name="price" fromKey="price_min" toKey="price_max" value={value} onChange={onChange} options={PRICE_STEPS} render={(p) => formatPriceShort(p, lang)} />;
};

export const MileageField = ({ value, onChange, idPrefix }) => {
  const t = useT();
  return (
    <div>
      <FieldLabel htmlFor={`${idPrefix}-mileage`}>{t("filters.mileage")}</FieldLabel>
      <NativeSelect id={`${idPrefix}-mileage`} testId={`${idPrefix}-mileage-select`} value={value.mileage_max} onChange={(v) => onChange({ mileage_max: v })}>
        <option value="">{t("filters.any")}</option>
        {MILEAGE_STEPS.map((m) => <option key={m} value={m}>{fmt(t("filters.upTo"), { value: `${m.toLocaleString("en-US")} km` })}</option>)}
      </NativeSelect>
    </div>
  );
};

export const ChipGroup = ({ label, field, enumKey, options = [], value, onChange, idPrefix, single = field === "market" }) => {
  const t = useT();
  const lang = useLocale();
  const selected = value[field] ? value[field].split(",") : [];
  const toggle = (v) => {
    const next = selected.includes(v) ? selected.filter((s) => s !== v) : single ? [v] : [...selected, v];
    onChange({ [field]: next.join(",") });
  };
  if (!options.length) return null;
  return (
    <fieldset>
      <legend className="micro-label mb-3 opacity-55">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = selected.includes(o.value);
          const empty = !on && o.count === 0;
          const text = o.label || o[`label_${lang}`] || t(`enums.${enumKey || field}.${o.value}`);
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(o.value)}
              disabled={empty}
              data-testid={`${idPrefix}-${field}-${o.value}`}
              className={`inline-flex min-h-[44px] items-center gap-2 border px-3.5 text-[13px] transition-colors duration-300 ${
                on ? "border-ink bg-ink text-white" : empty ? "cursor-not-allowed border-ink/10 text-ink/30" : "border-ink/20 text-ink hover:border-ink/60"
              }`}
            >
              {text.startsWith("enums.") ? o.value : text}
              <span className={`tabular-nums ${on ? "text-white/55" : "text-ink/40"}`}>{o.count}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
};

export const SoldToggle = ({ value, onChange, idPrefix }) => {
  const t = useT();
  const on = value.availability === "all";
  return (
    <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm" data-testid={`${idPrefix}-include-sold`}>
      <input type="checkbox" checked={on} onChange={() => onChange({ availability: on ? "" : "all" })} className="h-4 w-4 accent-[#0a0a0a]" />
      {t("filters.includeSold")}
    </label>
  );
};

export const MoreFields = ({ value, onChange, facets, idPrefix }) => {
  const t = useT();
  return (
    <div className="grid gap-6">
      <ChipGroup label={t("filters.fuel")} field="fuel" enumKey="fuel_type" options={facets?.fuel_types} value={value} onChange={onChange} idPrefix={idPrefix} />
      <ChipGroup label={t("filters.transmission")} field="transmission" enumKey="transmission" options={facets?.transmissions} value={value} onChange={onChange} idPrefix={idPrefix} />
      <ChipGroup label={t("filters.drivetrain")} field="drivetrain" enumKey="drivetrain" options={facets?.drivetrains} value={value} onChange={onChange} idPrefix={idPrefix} />
      <SoldToggle value={value} onChange={onChange} idPrefix={idPrefix} />
    </div>
  );
};
